import React from "react";

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: "horizontal" | "vertical";
  variant?: "solid" | "dashed" | "dotted";
  color?: "default" | "strong" | "subtle";
  spacing?: "none" | "sm" | "md" | "lg";
  label?: React.ReactNode;
}

/**
 * Arc-style Divider component
 * Replaces MUI Divider
 * 
 * Features:
 * - Horizontal and vertical orientations
 * - Multiple line styles
 * - Optional label/text
 * - Configurable spacing
 */
const Divider: React.FC<DividerProps> = ({
  orientation = "horizontal",
  variant = "solid",
  color = "default",
  spacing = "md",
  label,
  className = "",
  ...props
}) => {
  // Color styles
  const colorStyles = {
    default: "border-arc-border",
    strong: "border-arc-border-strong",
    subtle: "border-arc-border-subtle",
  };

  // Variant styles
  const variantStyles = {
    solid: "border-solid",
    dashed: "border-dashed",
    dotted: "border-dotted",
  };

  // Spacing styles
  const spacingStyles = {
    none: "",
    sm: orientation === "horizontal" ? "my-2" : "mx-2",
    md: orientation === "horizontal" ? "my-4" : "mx-4",
    lg: orientation === "horizontal" ? "my-6" : "mx-6",
  };

  // Horizontal divider with label
  if (orientation === "horizontal" && label) {
    return (
      <div
        className={`
          flex items-center
          ${spacingStyles[spacing]}
          ${className}
        `}
        role="separator"
        {...props}
      >
        <div
          className={`
            flex-1 border-t
            ${colorStyles[color]}
            ${variantStyles[variant]}
          `}
        />
        <span className="px-3 text-arc-sm text-content-tertiary font-medium">
          {label}
        </span>
        <div
          className={`
            flex-1 border-t
            ${colorStyles[color]}
            ${variantStyles[variant]}
          `}
        />
      </div>
    );
  }

  // Horizontal divider
  if (orientation === "horizontal") {
    return (
      <hr
        className={`
          border-t
          ${colorStyles[color]}
          ${variantStyles[variant]}
          ${spacingStyles[spacing]}
          ${className}
        `}
        role="separator"
        {...props}
      />
    );
  }

  // Vertical divider
  return (
    <div
      className={`
        self-stretch border-l
        ${colorStyles[color]}
        ${variantStyles[variant]}
        ${spacingStyles[spacing]}
        ${className}
      `}
      role="separator"
      {...props}
    />
  );
};

export default Divider;









